import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiError } from "../lib/api";
import { Header } from "../components/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { PasswordInput } from "../components/ui/password-input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

// Demo affordances (Quick-Fill buttons + the "DEMO LOGINS" panel) are a
// DEV-ONLY convenience. They are gated behind an explicit OPT-IN flag so the
// default — including every production build, which never sets this flag — is
// to HIDE them. Fail-safe by design: a missing/!=true env var means no demo
// credentials are ever surfaced on the login screen. Also hostname-guarded so
// the live production domain is always clean (see lib/demoMode.js).
import { SHOW_DEMO_LOGINS } from "../lib/demoMode";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  // When the backend returns 403 email_not_verified, surface a friendly
  // resend affordance inline instead of a generic error toast.
  const [unverifiedEmail, setUnverifiedEmail] = useState("");
  const [resending, setResending] = useState(false);

  // Never persist a password across sessions/reloads: clear app-managed password
  // state on mount and on unmount (defends against any stale/injected value).
  useEffect(() => {
    setPassword("");
    return () => setPassword("");
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setUnverifiedEmail("");
    try {
      const u = await login(email, password);
      toast.success(`Welcome, ${u.name}`);
      navigate(redirectFor(u.role));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 403 && detail?.reason === "email_not_verified") {
        // Show structured unverified-state UX so the user has a clear next step.
        setUnverifiedEmail(detail.email || email);
        toast.error(detail.message || "Email not verified — check your inbox.");
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setLoading(false);
    }
  };

  // Security: clear the password whenever the email/identity changes. This
  // ensures a password typed for one account can never be revealed (via the
  // show-password eye) against a different account, and also defeats the
  // browser password-manager "reveal previous password after switching
  // email" vector on shared/operator devices.
  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setPassword("");
    setUnverifiedEmail("");
  };

  const handleResend = async () => {
    if (!unverifiedEmail) return;
    setResending(true);
    try {
      await api.post("/auth/verify-resend", { email: unverifiedEmail });
      toast.success("Verification email sent — check your inbox.");
    } catch (e) {
      toast.error(formatApiError(e) || "Could not resend right now.");
    } finally {
      setResending(false);
    }
  };

  const quickFill = (email) => {
    setEmail(email);
    setPassword("demo123");
  };

  return (
    <div className="min-h-screen bg-background">
      <Header title="SIGN IN" />
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[calc(100vh-72px)]">
        <div className="hidden lg:block bg-[#0A0A0A] text-white relative overflow-hidden border-r border-black">
          <div className="absolute inset-0 opacity-30" style={{
            backgroundImage: 'radial-gradient(rgba(255,193,7,0.4) 1px, transparent 1px)',
            backgroundSize: '30px 30px',
          }} />
          <div className="relative h-full flex flex-col justify-between p-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                {/* Crystal Dispatch logo — visible login artwork.
                    Adds a strong brand cue above the headline on the
                    dark hero panel. Sized 56px so it pairs with the
                    "CRYSTAL DISPATCH / V2" eyebrow without dominating
                    the headline below. */}
                <img
                  src="/pwa-icon-192.png"
                  alt="Crystal Dispatch"
                  width={56}
                  height={56}
                  className="w-14 h-14 rounded-sm shadow-lg"
                  loading="eager"
                  decoding="async"
                  data-testid="login-hero-logo"
                />
                <div className="label-mini text-[#FFC107]">CRYSTAL DISPATCH / V2</div>
              </div>
              <h2 className="font-heading text-5xl font-black tracking-tighter leading-tight max-w-md">
                Pick up the<br />
                <span className="text-[#FFC107]">rhythm</span> of<br />
                the city.
              </h2>
            </div>
            {SHOW_DEMO_LOGINS && (
            <div className="space-y-2">
              <div className="label-mini text-white/50">DEMO LOGINS</div>
              <div className="font-mono-tab text-sm space-y-1 text-white/80">
                <div>passenger@demo.com</div>
                <div>john.driver@test.com</div>
                <div>admin@demo.com</div>
                <div className="text-[#FFC107] mt-2">pw: demo123</div>
              </div>
            </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-center p-6 md:p-12 bg-white">
          <form onSubmit={handleSubmit} className="w-full max-w-md space-y-6" data-testid="login-form">
            <div>
              <div className="label-mini text-muted-foreground mb-2">ACCESS</div>
              <h1 className="font-heading text-4xl font-black tracking-tight">Sign in</h1>
              <p className="text-sm text-muted-foreground mt-2">Welcome back to the network.</p>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="email" className="label-mini">EMAIL</Label>
                <Input
                  id="email"
                  type="email"
                  name="email"
                  autoComplete="username"
                  value={email}
                  onChange={handleEmailChange}
                  required
                  placeholder="name@example.com"
                  data-testid="login-email-input"
                  className="mt-1.5"
                />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="label-mini">PASSWORD</Label>
                  <Link
                    to="/forgot-password"
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="login-forgot-password-link"
                  >
                    Forgot password?
                  </Link>
                </div>
                <PasswordInput
                  id="password"
                  name="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  data-testid="login-password-input"
                  className="mt-1.5"
                />
              </div>
            </div>

            <Button type="submit" disabled={loading} className="w-full" size="lg" data-testid="login-submit-btn">
              {loading ? "Signing in…" : "Sign in"}
            </Button>

            {unverifiedEmail && (
              <div className="border-l-4 border-[#F59E0B] bg-[#FFFBEB] p-3 text-sm space-y-2" data-testid="login-unverified-banner">
                <div className="font-semibold text-[#92400E]">Email not verified</div>
                <div className="text-xs text-[#92400E]">
                  We sent a link to <strong>{unverifiedEmail}</strong>. Click it to activate your account.
                </div>
                <Button
                  type="button" variant="outline" size="sm"
                  onClick={handleResend} disabled={resending}
                  className="w-full"
                  data-testid="login-resend-verification-btn"
                >
                  {resending ? "Sending…" : "Resend verification email"}
                </Button>
              </div>
            )}

            {SHOW_DEMO_LOGINS && (
            <div className="border-t border-border pt-4 space-y-2">
              <div className="label-mini text-muted-foreground">QUICK FILL</div>
              <div className="grid grid-cols-3 gap-2">
                <button type="button" onClick={() => quickFill("passenger@demo.com")} className="text-xs py-2 border border-border hover:bg-secondary transition-colors" data-testid="quick-passenger">Passenger</button>
                <button type="button" onClick={() => quickFill("john.driver@test.com")} className="text-xs py-2 border border-border hover:bg-secondary transition-colors" data-testid="quick-driver">Driver</button>
                <button type="button" onClick={() => quickFill("admin@demo.com")} className="text-xs py-2 border border-border hover:bg-secondary transition-colors" data-testid="quick-admin">Admin</button>
              </div>
            </div>
            )}

            <div className="text-sm text-muted-foreground text-center">
              No account?{" "}
              <Link to="/register" className="text-[#0A0A0A] font-semibold underline underline-offset-2" data-testid="link-register">
                Create one
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export function redirectFor(role) {
  if (role === "admin") return "/admin";
  if (role === "driver") return "/driver";
  return "/passenger";
}
